import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function RequisitionManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const tabs = [
    { id: 'all', name: 'All Requisitions', icon: '📋', badge: requisitions.length },
    { id: 'pending', name: 'Pending Approval', icon: '⏳', badge: requisitions.filter(r => r.status === 'Pending_Approval').length },
    { id: 'approved', name: 'Approved', icon: '✅', badge: requisitions.filter(r => r.status === 'Approved').length },
    { id: 'trc', name: 'TRC Requests', icon: '🔧', badge: requisitions.filter(r => r.type === 'TRC_Spare_Parts').length }
  ];
  
  useEffect(() => {
    fetchRequisitions();
  }, [activeTab]);
  
  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab === 'pending') params.status = 'Pending_Approval';
      if (activeTab === 'approved') params.status = 'Approved';
      if (activeTab === 'trc') params.type = 'TRC_Spare_Parts';
      
      const response = await axios.get(`${API}/api/requisitions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params
      });
      setRequisitions(response.data.requisitions || []);
    } catch (error) {
      console.error('Error fetching requisitions:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };
  
  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending_Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Partially_Approved': 'bg-blue-100 text-blue-800',
      'Rejected': 'bg-red-100 text-red-800',
      'PO_Created': 'bg-purple-100 text-purple-800',
      'Parts_Issued': 'bg-teal-100 text-teal-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };
  
  const getTypeBadge = (type) => {
    const colors = {
      'TRC_Spare_Parts': 'bg-orange-100 text-orange-800',
      'Consumption': 'bg-blue-100 text-blue-800',
      'Packaging': 'bg-green-100 text-green-800',
      'Capital': 'bg-purple-100 text-purple-800',
      'Others': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type.replace(/_/g, ' ')}
      </span>
    );
  };
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: ZOBOX_PRIMARY }}>
              Requisition Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage TRC spare parts and general requisitions
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: ZOBOX_PRIMARY }}
          >
            <span className="text-xl">+</span>
            Create Requisition
          </button>
        </div>
        
        {/* Tabs */}
        <div className="border-b mb-6">
          <div className="flex gap-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-3 font-medium transition-colors relative flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-b-2 text-[#2b3380]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === tab.id ? { borderColor: ZOBOX_PRIMARY } : {}}
              >
                <span>{tab.icon}</span>
                {tab.name}
                {tab.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#2b3380]"></div>
            <p className="mt-4 text-gray-600">Loading requisitions...</p>
          </div>
        ) : requisitions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Requisitions Found</h3>
            <p className="text-gray-600 mb-4">
              Create your first requisition to get started
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 text-white rounded-lg font-medium"
              style={{ backgroundColor: ZOBOX_PRIMARY }}
            >
              Create Requisition
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Req #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estimated Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requisitions.map((req) => (
                  <tr key={req.requisition_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#2b3380]">{req.requisition_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(req.type)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {req.device ? (
                          <>
                            <div className="font-medium">{req.device.brand} {req.device.model}</div>
                            <div className="text-xs text-gray-500">DID: {req.device.did || req.device.supplier_ref}</div>
                          </>
                        ) : (
                          <div>{req.department || 'General'}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {req.spare_parts?.length || req.items?.length || 0} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{req.estimated_total?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(req.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(req.created_at).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          if (req.status === 'Pending_Approval') {
                            navigate(`/requisition-approval/${req.requisition_id}`);
                          } else {
                            navigate(`/requisition-approval/${req.requisition_id}`);
                          }
                        }}
                        className="text-[#2b3380] hover:text-blue-900"
                      >
                        {req.status === 'Pending_Approval' ? 'Approve' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Modals will be added here */}
      </div>
    </Layout>
  );
}

export default RequisitionManagement;
